/**
 * Unterstützte UI-Sprachen.
 *
 * Bewusst als `as const`-Objekt + Union-Type modelliert (keine TS-Enums,
 * siehe Repo-Konvention). Erweiterung um TR/AR/RU ist vorgesehen
 * (`refined`-Hinweis in AFO-005), bedeutet aber zusätzlich JSON-Bundles
 * und Plural-Regeln.
 */
export const Locale = {
  De: 'de',
  En: 'en',
} as const;

export type Locale = (typeof Locale)[keyof typeof Locale];

/**
 * Default-Sprache, falls weder `sessionStorage` noch Browser-Hinweis greifen.
 */
export const DEFAULT_LOCALE: Locale = Locale.De;

/**
 * Liste aller unterstützten Locales — Quelle für Sprachumschalter und Guards.
 */
export const SUPPORTED_LOCALES: readonly Locale[] = [Locale.De, Locale.En];

/**
 * Type-Guard für eingehende, unbekannte Werte (z. B. aus `sessionStorage`).
 *
 * @param candidate Beliebiger Wert.
 */
export function isLocale(candidate: unknown): candidate is Locale {
  if (typeof candidate !== 'string') {
    return false;
  }
  return SUPPORTED_LOCALES.some((entry) => entry === candidate);
}

/**
 * Mappt einen BCP-47-Sprach-Tag (z. B. `navigator.language` wie `"en-US"`) auf
 * eine unterstützte Locale. Verglichen wird nur der primäre Subtag, case-
 * insensitiv (`"de-DE"` → `de`, `"EN"` → `en`). Nicht unterstützte Sprachen
 * (`"fr-FR"`, `""`) liefern `undefined`, damit der Aufrufer weiterfallen kann.
 *
 * @param languageTag Sprach-Tag aus der Browser-Umgebung.
 */
export function matchSupportedLocale(languageTag: string): Locale | undefined {
  const primarySubtag = (languageTag.split('-')[0] ?? '').toLowerCase();
  return SUPPORTED_LOCALES.find((entry) => entry === primarySubtag);
}
